import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Gamepad2, AlertTriangle } from "lucide-react";

/* Steam Page Kit — the complete copy deck for the "New Citizen — Alpha" Steam store page.
   Long description uses Steam's store markup ([h2], [list], [b], [i]). Each block copies
   to clipboard. Text-only deliverable: store assets (capsule images) still need to be
   uploaded in Steamworks — see the checklist at the bottom. */

const BLOCKS = [
  {
    id: "short", title: "Short description (300 chars max)", chars: 300,
    text: "A living-city roleplay sandbox in Unreal Engine 5 where every system is player-run. Dispatch from a real police CAD, crack vaults in tactile heists, run casino tables, mine crypto, grow and cook product for a network of NPC buyers — and get elected mayor. Write the laws. Break them.",
  },
  {
    id: "long", title: "Long description (Steam markup — paste into the store editor)",
    text: `[img]{STEAM_APP_IMAGE}/extras/steam-menu.gif[/img]

[h2]WELCOME TO BORNWOOD[/h2]
[b]New Citizen[/b] is a hardcore roleplay sandbox set in a living Australian city — a standalone Unreal Engine 5 title, not a mod. Create a citizen, walk out of Central Station, and the city takes it from there. Every system is player-run: the officer dispatched to your robbery, the judge who hears your case, the mayor who sets your taxes — all people. All real.

[img]{STEAM_APP_IMAGE}/extras/steam-cad.gif[/img]

[h2]THE LAW IS A PLAYER[/h2]
[list]
[*] [b]A real police CAD/MDT[/b] — 911 calls land on a live dispatch board with an AI dispatcher that takes voice calls, units plotted on a beat map, NCIC lookups, BOLOs, and reports that file straight to court
[*] [b]Dispatch missions[/b] — pursuits, burglary alarms, welfare checks and buy-busts spawn into the CAD continuously, so a quiet night never stays quiet. Rewards wire to your bank on completion
[*] [b]A working courthouse[/b] — warrants, charges, verdicts and license suspensions, argued by players before a player judge
[*] [b]Player-elected government[/b] — register at City Hall, survive the vote, hold real power. Die in office and the seat opens for a special election
[/list]

[img]{STEAM_APP_IMAGE}/extras/steam-heist.gif[/img]

[h2]THE UNDERWORLD[/h2]
[list]
[*] [b]Tactile heists[/b] — a real three-notch safe dial with directional passes, four-digit keypad hacks, and high-security vaults that trip their alarm mid-crack
[*] [b]Crew roles[/b] — a second player runs crowd control while you work; if the room loses its nerve, the job blows
[*] [b]The Gilded Spade vault[/b] — the biggest take in the city, guarded by an alarm that calls the cops before you're done
[*] [b]Every job raises a 211[/b] — win or lose, the police CAD lights up. The chase is part of the game
[/list]

[img]{STEAM_APP_IMAGE}/extras/steam-grow.gif[/img]

[h2]THE TRADE[/h2]
[list]
[*] [b]Grow it[/b] — hydro tents and DIY cupboards with a live climate: temperature, humidity, light tiers, watering and feeding. Cure your harvest for exotic-grade prices. Forget the carbon filter and the neighbours start calling
[*] [b]Cook it[/b] — a 90-second bench cook where you hold the batch inside the crystal band. Push past 205°C and the lab goes up — along with a fire call on every radio in the city
[*] [b]Move it[/b] — a persistent network of NPC buyers reaches out with offers against your actual stock. Haggle, deal, build rep — and watch your heat, because one in a dozen contacts is a badge running a buy-bust
[/list]

[img]{STEAM_APP_IMAGE}/extras/steam-roulette.gif[/img]

[h2]THE GILDED SPADE[/h2]
A real casino running against your bank balance: European roulette on a full betting layout, the Liberty Belle three-reel cabinet, single-player blackjack, and [b]two-seat multiplayer tables[/b] where every hand is face-up and you can watch your rival sweat. High-roller room runs $500 minimums. The UI sits on the table — no menus, just felt.

[img]{STEAM_APP_IMAGE}/extras/steam-crypto.gif[/img]

[h2]CIVCOIN & THE GRIND[/h2]
Buy a rig, hash CivCoin, manage thermals, upgrade GPUs and cooling — and sell into a market that drifts between $8 and $60. Leave your rig hashing unattended and rivals can [b]short-circuit it for a cut of your buffer[/b]. Then there's the honest work: courier, tow, taxi, fishing, real estate.

[h2]YOUR PHONE[/h2]
Banking, transfers, messages, Buzzer (the city's social feed), the market, City Hall — and a working 911 line with an AI dispatcher that takes voice calls and routes units in real time.

[h2]PERSISTENT NPCs WITH RECORDS[/h2]
The NCDB is a network criminal database: persistent NPCs with mugshots, rap sheets and case files that follow them across servers. Spot one, report one, arrest one — the file grows.

[h2]RUN YOUR OWN WORLD[/h2]
[list]
[*] Dedicated server files via SteamCMD — self-host free, or rent
[*] [b]NC Bridge[/b] — an auto-detecting sync agent wiring banking, roster, DMV and police state between your database and the city
[*] [b]Mod Workshop[/b] — one-click JSON packs: menu themes, boot sequences, penal codes, departments, whole governments
[*] [b]Owner Deck[/b] — economy, rulesets, laws, entry exams. [b]Creator Studio[/b] — in-game recording, broadcast overlays and clip markers for streamers
[/list]

[h2]SIX LANGUAGES AT LAUNCH[/h2]
Menu, police tablet and phone ship in English, Spanish, German, French, Portuguese (BR) and Russian.

[i]New Citizen is in Early Access and entirely self-funded by its founder, a disability support worker who builds it one shift at a time. You're not buying a finished city — you're becoming one of its first citizens.[/i]

[img]{STEAM_APP_IMAGE}/extras/steam-phone.gif[/img]`,
  },
  {
    id: "ea-why", title: "Early Access — “Why Early Access?”",
    text: "New Citizen is a living-city platform, and living cities get built around their residents. Every core system — dispatch, courts, elections, heists, the casino, crypto — works better when real players stress it, bend it and tell us what the city needs next. Early Access lets the founding citizens shape the city while we build it. The game is entirely self-funded by founder Tanner Rozankovic, who finances development from his wages working as a disability support worker — there is no publisher deadline here, only the community's.",
  },
  {
    id: "ea-howlong", title: "Early Access — “Approximately how long…?”",
    text: "We expect New Citizen to remain in Early Access for roughly 18–24 months, with major systems shipping continuously through that window rather than in one launch-day drop.",
  },
  {
    id: "ea-fullversion", title: "Early Access — “How is the full version planned to differ?”",
    text: "The full version is planned to expand every pillar: more heist targets and crew roles, deeper politics (city budgets, departments beyond the mayor), property and business ownership at scale, the medical triage system, the deep web, live car radios, corporate takeovers on the market, a full creator economy (blueprint sharing, livery painter, mod workshop for everyone), and the global traveler system linking servers into one network of cities.",
  },
  {
    id: "ea-current", title: "Early Access — “What is the current state?”",
    text: "Playable today: character creation and spawn selection with faction whitelist support, the cinematic server join, phone (banking, messages, Buzzer, market, City Hall, AI 911 dispatch with voice), police CAD/MDT (units, beats, reports to court, warrants), the courthouse and DMV, elections and the mayor death rule, The Gilded Spade casino (roulette, slots, blackjack, high-roller room), CivCoin mining with rig upgrades, heists (safe dial, keypad hack, crew crowd control), the in-game admin console, the Mod Workshop with installable packs, the Owner Deck for server owners, and six-language localization of the menu, tablet and phone.",
  },
  {
    id: "ea-price", title: "Early Access — “Will the game be priced differently?”",
    text: "Yes. The Early Access price is intentionally lower as a thank-you to founding citizens. The price will rise as major systems land, with the biggest step at full release.",
  },
  {
    id: "ea-community", title: "Early Access — “How are you planning on involving the community?”",
    text: "The Discord and in-game city wire are the direct line: bug reports, feature votes, and server-owner feedback shape the roadmap publicly (the roadmap page on our site tracks milestones live). Modders get the Mod Hub suite; server owners get the Owner Deck and bridge tooling; creators get the in-game Creator Studio with recording and broadcast overlays.",
  },
  {
    id: "sysreq", title: "System requirements (Minimum / Recommended)",
    text: `MINIMUM: OS: Windows 10 64-bit · CPU: 6-core (Ryzen 5 3600 / i5-10400) · RAM: 16 GB · GPU: RTX 2060 / RX 6600 (6 GB) · DirectX 12 · Storage: 60 GB SSD · Broadband internet

RECOMMENDED: OS: Windows 11 64-bit · CPU: 8-core (Ryzen 7 5800X / i7-12700) · RAM: 32 GB · GPU: RTX 4070 / RX 7800 XT (12 GB) · DirectX 12 · Storage: 60 GB NVMe SSD · Broadband internet`,
  },
  {
    id: "tags", title: "Tags (comma-separated — paste into the tag field)",
    text: "RPG, Life Sim, Immersive Sim, Open World, Multiplayer, Crime, Sandbox, Role-Playing, Economy, Realistic, Simulation, Online Co-Op, Political Sim, Detective, Moddable, Early Access, Unreal Engine 5, Heist, Driving, First-Person",
  },
  {
    id: "announce", title: "Update announcement — “Alpha 4.0: The Living City Update”",
    text: `[h2]ALPHA 4.0 — THE LIVING CITY UPDATE[/h2]
The biggest drop since Early Access began:

[list]
[*] [b]The Gilded Spade[/b] — the city casino opens: European roulette, Liberty Belle slots, blackjack, and a $500-minimum high-roller room. Its vault is also the city's biggest heist
[*] [b]Heists 2.0[/b] — tactile safecracking and keypad hacks, plus crew roles: a second player runs crowd control while you crack
[*] [b]CivCoin mining[/b] — buy a rig, manage heat, upgrade GPUs and cooling, and sell CIV into a drifting market
[*] [b]A new arrival experience[/b] — cinematic server entry with a "while you were away" city recap, character creation, spawn selection and faction picks
[*] [b]The mayor death rule[/b] — die in office and the seat opens for a special election (server-optional persistence)
[*] [b]Admin console 2.0[/b] — transparent side-dock design, player Steam/IP cards, and network-wide watch reports reviewed by studio staff
[*] [b]Six languages[/b] — menu, police tablet and phone now in EN, ES, DE, FR, PT-BR and RU
[/list]

Full patch notes and the public roadmap: specpixel.com — see you in Bornwood.`,
  },
];

function Block({ b }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300">{b.title}</p>
        {b.chars && <span className={`font-mono text-[9px] ${b.text.length > b.chars ? "text-red-400" : "text-zinc-600"}`}>{b.text.length}/{b.chars}</span>}
        <button data-testid={`steam-copy-${b.id}`} onClick={() => { navigator.clipboard.writeText(b.text); setCopied(true); toast.success("Copied — paste it into Steamworks"); setTimeout(() => setCopied(false), 1600); }}
          className="ml-auto flex items-center gap-1.5 border border-white/15 hover:border-nc-blue px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300 transition-colors">
          {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />} Copy
        </button>
      </div>
      <pre data-testid={`steam-block-${b.id}`} className="font-mono text-[10px] text-zinc-400 leading-relaxed whitespace-pre-wrap break-words bg-black/40 border border-white/5 p-4 max-h-72 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>{b.text}</pre>
    </div>
  );
}

export default function AdminSteamKit() {
  return (
    <div data-testid="admin-steamkit" className="flex flex-col gap-4 max-w-4xl">
      <div className="border border-white/10 bg-[#0a0a0a] p-4">
        <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase flex items-center gap-2">
          <Gamepad2 className="w-3.5 h-3.5 text-nc-blue" /> Steam store copy — “New Citizen — Alpha”
        </p>
        <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
          The full store-page copy deck for the Alpha 4.0 update, paste-ready for Steamworks
          (Store Page → Edit). Long description uses Steam markup — <span className="text-zinc-300">[h2], [list], [b], [i]</span> render natively in the store editor.
        </p>
        <p className="font-mono text-[10px] text-amber-400/90 mt-2 leading-relaxed flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Capsule art is generated and ready — press kit page → “Steam capsule set” (all six Steamworks sizes as a zip). Upload in Steamworks → Graphical Assets. Showcase GIFs for the About section are at <span className="text-zinc-200">/assets/steam/</span> (steam-menu / steam-cad / steam-heist / steam-grow / steam-roulette / steam-crypto / steam-phone) — upload each into the store editor where the [img] slots sit in the long description.
        </p>
      </div>
      {BLOCKS.map((b) => <Block key={b.id} b={b} />)}
    </div>
  );
}
